/**
 * Adaptive value input with enum suggestions
 * Shows a combobox when enumValues are available, plain input otherwise
 */

import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface ValueComboboxProps {
  value: string
  onChange: (value: string) => void
  enumValues?: string[]
  placeholder?: string
  className?: string
}

export function ValueCombobox({
  value,
  onChange,
  enumValues,
  placeholder = 'Value',
  className,
}: ValueComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  if (!enumValues || enumValues.length === 0) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn('h-8 font-mono text-sm', className)}
      />
    )
  }

  const hasExactMatch = enumValues.some(
    (v) => v.toLowerCase() === search.toLowerCase()
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 justify-between font-mono text-sm', className)}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search values..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {search ? (
                <button
                  className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded cursor-pointer"
                  onClick={() => {
                    onChange(search)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  Use: <span className="font-mono font-medium">{search}</span>
                </button>
              ) : (
                'No values found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              {enumValues.map((enumVal) => (
                <CommandItem
                  key={enumVal}
                  value={enumVal}
                  onSelect={() => {
                    onChange(enumVal)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === enumVal ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="font-mono text-sm">{enumVal}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {search && !hasExactMatch && (
              <CommandGroup heading="Custom">
                <CommandItem
                  value={`custom-${search}`}
                  onSelect={() => {
                    onChange(search)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  Use custom: <span className="font-mono font-medium ml-1">{search}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
